import { TimePicker } from "@/components/blocks/time-picker";
import { EDSDatePicker } from "@/components/eds/EDSDatePicker";
import { EDSQuantityStepper } from "@/components/eds/EDSQuantityStepper";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { ViewBooking } from "collins-js-sdk";
import { ChevronDown, Search } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useUserContext } from "../../../../../hooks/useUserContext";
import {
  getBookingDuration,
  getFormattingOptions
} from "../../../../../utils/booking";
import { addDateTimeOffset } from "../../../../../utils/helpers";
import { getBookingEndTimeAsString } from "../../../../../utils/singleBooking";
import { getDurationData } from "../../Sidebar/AddBooking/AddBookingController";
import { WebViewPopover } from "../../SubHeader/WebViewPopover";

type EditBookingDetailsType = {
  booking: ViewBooking;
  updateBooking: (bookingProps: Partial<ViewBooking>) => void;
};

const EditBookingDetails = ({
  booking,
  updateBooking
}: EditBookingDetailsType) => {
  const [isVenuePickerOpen, setIsVenuePickerOpen] = useState<boolean>(false);
  const [isTypePickerOpen, setIsTypePickerOpen] = useState<boolean>(false);
  const [venueSearchCriteria, setVenueSearchCriteria] = useState<string>("");
  const [typeSearchCriteria, setTypeSearchCriteria] = useState<string>("");
  const [venueData, setVenueData] = useState<
    { label: string; value: string }[]
  >([]);
  const portalContainerRef = useRef<HTMLDivElement>(null);
  const { venueUserConfig } = useAppContext();
  const { venues: allVenues, venueConfig } = useDiaryContext();
  const { user } = useUserContext();
  const untilTime = getBookingEndTimeAsString(booking);

  const venues = useMemo(() => {
    if (!allVenues || !venueUserConfig) {
      return [];
    }

    if (venueUserConfig.venues) {
      return allVenues.filter((venue) => !!venue.title);
    }

    if (Array.isArray(venueUserConfig.venues)) {
      return allVenues.filter(
        (venue) =>
          Array.isArray(venueUserConfig.venues) &&
          venueUserConfig.venues.indexOf(venue._id) > -1 &&
          venue.title
      );
    }

    return [];
  }, [allVenues, venueUserConfig]);

  useEffect(() => {
    if (venues) {
      setVenueData(
        venues.map((venue) => ({
          label: venue.title,
          value: venue._id
        }))
      );
    }
  }, [venues]);

  const filteredVenues = useMemo(() => {
    if (!venueSearchCriteria) return venueData;
    return venueData.filter((venue) =>
      venue.label.toLowerCase().includes(venueSearchCriteria.toLowerCase())
    );
  }, [venueData, venueSearchCriteria]);

  const selectedVenueLabel = useMemo(() => {
    return (
      venueData.find((venue) => venue.value === booking.venueId)?.label ??
      "Select a venue"
    );
  }, [venueData, booking.venueId]);

  const bookingTypes = useMemo(() => {
    return (
      venueConfig?.booking_types
        ?.filter((bt) => !bt.archived)
        .map((bt) => ({
          label: bt.name,
          value: bt.id
        })) ?? []
    );
  }, [venueConfig]);

  const filteredTypes = useMemo(() => {
    if (!typeSearchCriteria) return bookingTypes;
    return bookingTypes.filter((type) =>
      type.label.toLowerCase().includes(typeSearchCriteria.toLowerCase())
    );
  }, [bookingTypes, typeSearchCriteria]);

  const selectedTypeLabel = useMemo(() => {
    return booking.type?.name ?? "Select a Type";
  }, [booking.type]);

  const durationData = useMemo(() => getDurationData(), []);

  const [timePickerPortalTarget, setTimePickerPortalTarget] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    const host = portalContainerRef.current;
    if (!host) return;

    let overlay: HTMLElement | null = null;
    let current = host.parentElement;
    while (current) {
      if (
        current.className &&
        typeof current.className === "string" &&
        current.className.includes("ReactModal__Overlay")
      ) {
        overlay = current;
        break;
      }
      current = current.parentElement;
    }

    const modalRoot = host.closest(".DetachedModalPortal");
    const target = (overlay ?? modalRoot ?? host) as HTMLElement;

    setTimePickerPortalTarget(target);
  }, []);

  return (
    <div className="EditBookingDetails" ref={portalContainerRef}>
      <div className="EditBookingRow flex py-panel-padding max-[600px]:flex-col">
        <div className="EditBookingFormSection NoBorder relative min-h-[37px] w-1/2 max-[600px]:w-full flex items-center justify-between mr-2 max-[600px]:mr-0 border-b-0">
          <p className="EditBookingFormLabel text-left font-bold text-collins-form-label text-form-label m-0 pr-2">
            Venue
          </p>
          <WebViewPopover
            open={isVenuePickerOpen}
            onOpenChange={setIsVenuePickerOpen}
            side="bottom"
            sideOffset={8}
            content={
              <>
                <div className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search Venues"
                      value={venueSearchCriteria}
                      onChange={(e) => setVenueSearchCriteria(e.target.value)}
                      className="pl-10 bg-background border border-input rounded-md text-sm font-normal h-9"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto px-2 pb-2 ">
                  {filteredVenues.length > 0 ? (
                    filteredVenues.map((venue) => {
                      const isSelected = venue.value === booking.venueId;
                      return (
                        <button
                          key={venue.value}
                          className={`w-full px-3 py-2 text-left text-sm focus:outline-none rounded-md ${
                            isSelected
                              ? "bg-accent text-accent-foreground font-medium"
                              : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          }`}
                          onClick={() => {
                            const newVenueId = venue.value;
                            if (newVenueId && newVenueId !== booking.venueId) {
                              updateBooking({
                                venueId: newVenueId,
                                assignedAreas: [],
                                type: null
                              });
                            }
                            setIsVenuePickerOpen(false);
                            setVenueSearchCriteria("");
                          }}
                        >
                          {venue.label}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No venues found
                    </div>
                  )}
                </div>
              </>
            }
          >
            <button className="eds-button flex items-center justify-between w-full bg-background border !border-[hsl(var(--border))] border-solid rounded-md text-sm font-normal px-3 py-2 h-9 hover:bg-accent hover:text-accent-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 w-60">
              <span className="truncate text-left">{selectedVenueLabel}</span>
              <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
            </button>
          </WebViewPopover>
        </div>
        <div className="EditBookingFormSection TagMobileDateInput NoBorder relative min-h-[37px] w-1/2 max-[600px]:w-full flex items-center gap-2 ml-2 max-[600px]:ml-0 border-b-0 justify-between">
          <p className="EditBookingFormLabel text-left font-bold text-collins-form-label text-form-label m-0 pr-2">
            Date
          </p>
          <EDSDatePicker
            className="w-60 items-end"
            date={booking.date ? new Date(booking.date) : undefined}
            onSelect={(selectedDate) => {
              if (selectedDate) {
                updateBooking({
                  date: addDateTimeOffset(selectedDate)
                });
              } else {
                updateBooking({
                  date: new Date(booking.date ?? "")
                });
              }
            }}
            placeholder="Select date"
          />
        </div>
      </div>
      <div className="EditBookingRow flex py-panel-padding max-[600px]:flex-col">
        <div className="EditBookingFormSection relative min-h-[37px] w-1/2 max-[600px]:w-full flex items-center justify-between mr-2 max-[600px]:mr-0">
          <p className="EditBookingFormLabel text-left font-bold text-collins-form-label text-form-label m-0 pr-2">
            Type
          </p>
          <WebViewPopover
            open={isTypePickerOpen}
            onOpenChange={setIsTypePickerOpen}
            side="bottom"
            sideOffset={8}
            content={
              <>
                <div className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search Booking Types"
                      value={typeSearchCriteria}
                      onChange={(e) => setTypeSearchCriteria(e.target.value)}
                      className="pl-10 bg-background border border-input rounded-md text-sm font-normal h-9"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto px-2 pb-2">
                  {filteredTypes.length > 0 ? (
                    filteredTypes.map((type) => {
                      const isSelected = type.value === booking.type?.id;
                      return (
                        <button
                          key={type.value}
                          className={`w-full px-3 py-2 text-left text-sm focus:outline-none rounded-md  ${
                            isSelected
                              ? "bg-accent text-accent-foreground font-medium"
                              : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          }`}
                          onClick={() => {
                            if (type.value !== booking.type?.id) {
                              updateBooking({
                                type: {
                                  id: type.value,
                                  name: type.label
                                }
                              });
                            }
                            setIsTypePickerOpen(false);
                            setTypeSearchCriteria("");
                          }}
                        >
                          {type.label}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No booking types found
                    </div>
                  )}
                </div>
              </>
            }
          >
            <button className="eds-button flex items-center justify-between w-full bg-background border !border-[hsl(var(--border))] border-solid rounded-md text-sm font-normal px-3 py-2 h-9 hover:bg-accent hover:text-accent-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 w-60 ">
              <span className="truncate text-left">{selectedTypeLabel}</span>
              <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
            </button>
          </WebViewPopover>
        </div>
        <div className="EditBookingFormSection NoBorder relative min-h-[37px] w-1/2 max-[600px]:w-full flex items-center justify-between ml-2 max-[600px]:ml-0 border-b-0">
          <p className="EditBookingFormLabel text-left font-bold text-collins-form-label text-form-label m-0 pr-2">
            Guests
          </p>
          <EDSQuantityStepper
            value={Number(booking?.actualGuests) || 1}
            onChange={(newValue) => {
              let actualGuests = booking.actualGuests;
              const numPeople = newValue;
              if (booking.actualGuests === booking.numPeople) {
                actualGuests = numPeople;
              }
              updateBooking({
                numPeople,
                actualGuests
              });
            }}
            min={1}
            max={999}
            step={1}
          />
        </div>
      </div>
      <div className="EditBookingRow flex py-panel-padding max-[600px]:flex-col">
        <div className="EditBookingFormSection relative min-h-[37px] w-1/2 max-[600px]:w-full flex items-center justify-between mr-2 max-[600px]:mr-0">
          <p className="EditBookingFormLabel text-left font-bold text-collins-form-label text-form-label m-0 pr-2">
            From
          </p>
          {booking.waitlisted === true && !booking.currentStage ? (
            <Input
              disabled={true}
              value={"ASAP"}
              type="text"
              className="bg-background border border-input rounded-md text-sm font-normal px-3 py-2 h-9 TagItem"
            />
          ) : (
            <TimePicker
              placeholder="Start Time"
              value={booking.time ?? ""}
              format={
                getFormattingOptions(user?.locale) === "HH:mm" ? "24h" : "12h"
              }
              onChange={(value) =>
                updateBooking({
                  time: value
                })
              }
              portalContainer={timePickerPortalTarget}
              className="w-60 flex gap-2"
            />
          )}
        </div>
        <div className="EditBookingFormSection relative min-h-[37px] w-1/2 max-[600px]:w-full flex items-center justify-between ml-2 max-[600px]:ml-0">
          <p className="EditBookingFormLabel text-left font-bold text-collins-form-label text-form-label m-0 pr-2">
            To
          </p>
          {booking.waitlisted === true && !booking.currentStage ? (
            <Select
              value={String(booking.duration ?? "")}
              onValueChange={(value) => {
                const numValue = Number(value);
                if (!isNaN(numValue) && booking.duration !== numValue) {
                  updateBooking({
                    duration: numValue
                  });
                }
              }}
            >
              <SelectTrigger className="w-full bg-background border border-input rounded-md text-sm font-normal h-9">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent container={portalContainerRef.current}>
                {durationData.map((duration) => (
                  <SelectItem
                    key={duration.value}
                    value={String(duration.value)}
                  >
                    {duration.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <TimePicker
              value={untilTime}
              placeholder="End Time"
              format={
                getFormattingOptions(user?.locale) === "HH:mm" ? "24h" : "12h"
              }
              onChange={(value) =>
                updateBooking({
                  duration: getBookingDuration(
                    booking.time ?? undefined,
                    value ?? untilTime
                  )
                })
              }
              portalContainer={timePickerPortalTarget}
              className="w-60 flex gap-2"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo<EditBookingDetailsType>(EditBookingDetails);
